import { ChevronDown, ChevronRight } from "lucide-react";
import React from "react";
import Checkbox from "@/components/atomic/Checkbox";
import { cn } from "@/lib/utils";

export interface CategoryData {
  groups: string[];
  versions: string[];
}

export interface TreeNode {
  name: string;
  path: string;
  level: number;
  data?: CategoryData;
  children: TreeNode[];
  isExpanded?: boolean;
  isSelected?: boolean;
}

interface RenderNodeProps {
  node: TreeNode;
  isExpanded: (path: string) => boolean;
  isSelected: (path: string) => boolean;
  toggleExpand: (path: string) => void;
  toggleSelect: (path: string) => void;
  onVersionClick: (versions: string[]) => void;
  mode: "all" | "group";
}

// 最下層のノードかどうかを判定する関数
const isLeafNode = (node: TreeNode): boolean => {
  return node.children.length === 0;
};

// グループタグをレンダリングする関数
const renderGroupTags = (groups: string[], mode: "all" | "group") => (
  <div className="flex flex-col gap-2 justify-center h-full">
    {groups.map((group) => (
      <div
        key={group}
        className="bg-[#e8f3fc] border border-[#228be6] rounded-full px-3 py-1 inline-flex items-center justify-center"
        style={{ height: "26px", maxWidth: "fit-content" }}
      >
        <span className="text-[#228be6] text-xs whitespace-nowrap">
          {group}
        </span>
      </div>
    ))}
    {groups.length === 0 && mode === "all" && (
      <div
        className="bg-neutral-200 border border-neutral-200 rounded-full px-5 py-1 inline-flex items-center justify-center"
        style={{ height: "26px", width: "100px" }}
      >
        <span className="text-[#5f6368] text-xs text-center whitespace-nowrap">
          未割り当て
        </span>
      </div>
    )}
  </div>
);

const RenderNode: React.FC<RenderNodeProps> = ({
  node,
  isExpanded,
  isSelected,
  toggleExpand,
  toggleSelect,
  onVersionClick,
  mode,
}) => {
  const hasChildren = node.children.length > 0;
  const hasData = Boolean(node.data);
  const expanded = isExpanded(node.path);
  const indentation = (node.level - 1) * 32;

  return (
    <React.Fragment key={node.path}>
      <div className="flex border-t border-[#dee2e6]">
        {/* カテゴリ名 */}
        <div
          className={`flex items-center border-r border-[#dee2e6] ${isLeafNode(node) ? "bg-white" : "bg-[#fafbff]"}`}
          style={{ flex: 1, padding: "14px 16px 12px", paddingLeft: `${16 + indentation}px` }}
        >
          <div className="flex items-center gap-2">
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleExpand(node.path);
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                {expanded
                  ? <ChevronDown size={16} className="text-gray-600" />
                  : <ChevronRight size={16} className="text-gray-600" />}
              </button>
            )}
            {!hasChildren && (
              <div className={cn("h-6", mode === "all" ? "w-6" : "w-3")} /> // スペーサー
            )}

            <div className={`flex items-center gap-2`}>
              {mode === "all"
                ? (
                  <>
                    <Checkbox
                      checked={isSelected(node.path)}
                      onCheckedChange={() => toggleSelect(node.path)}
                      data-testid={`category-checkbox-${node.path}`}
                    />
                    <span
                      className={cn(
                        "text-sm text-[#333333] cursor-pointer",
                        !isLeafNode(node) ? "font-bold" : "font-normal",
                      )}
                      onClick={() => toggleSelect(node.path)}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === " " && toggleSelect(node.path)}
                      role="button"
                    >
                      {node.name}
                    </span>
                  </>
                )
                : (
                  <span
                    className={cn(
                      "text-sm text-[#333333]",
                      !isLeafNode(node) ? "font-bold" : "font-normal",
                    )}
                  >
                    {node.name}
                  </span>
                )}
            </div>
          </div>
        </div>

        {/* 現在のグループ */}
        <div
          className="flex items-start bg-white border-r border-[#dee2e6]"
          style={{ width: "264px", padding: "8px 16px" }}
        >
          {hasData && renderGroupTags(node.data!.groups, mode)}
        </div>

        {/* バージョン情報 */}
        <div
          className="flex items-center justify-center bg-white"
          style={{ width: "144px", padding: "8px 16px" }}
        >
          {hasData
            && (
              <button
                className="!text-sm text-[#6788e1] underline hover:text-blue-700"
                onClick={(e) => {
                  e.preventDefault();
                  onVersionClick(node.data!.versions);
                }}
              >
                バージョン情報
              </button>
            )}
        </div>
      </div>

      {/* 子ノードの再帰的レンダリング */}
      {expanded && node.children.map(child => (
        <RenderNode
          key={child.path}
          node={child}
          isExpanded={isExpanded}
          isSelected={isSelected}
          toggleExpand={toggleExpand}
          toggleSelect={toggleSelect}
          onVersionClick={onVersionClick}
          mode={mode}
        />
      ))}
    </React.Fragment>
  );
};

export default RenderNode;
